import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from "@mui/material";
import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    useGetUserQuery,
    useGetInflyxScoreQuery,
    useGetInfluencerTransactionHistoryQuery,
} from "../../redux/api";

const InfluencerInsightDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(20);

    const { data: userDetails, isLoading: isUserLoading } = useGetUserQuery(id);
    const { data: scoreDetails, isLoading: isScoreLoading } = useGetInflyxScoreQuery(id);
    const { data: transactionData, isLoading: isTransactionsLoading } = useGetInfluencerTransactionHistoryQuery({
        influencerId: id,
        limit,
        offset: page * limit,
    });

    if (isUserLoading || isScoreLoading) return <Typography sx={{ p: 3 }}>Loading...</Typography>;

    const influencer = userDetails?.result || {};
    const txResult = transactionData?.result || {};
    const transactions = txResult.transactions || [];

    const getStatusColor = (status) => {
        switch (status) {
            case 'Approved': return 'success';
            case 'Rejected': return 'error';
            case 'Pending': return 'warning';
            default: return 'default';
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'Credit': return '#4caf50';
            case 'Debit': return '#f44336';
            case 'Withdraw': return '#ff9800';
            default: return '#9e9e9e';
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button variant="outlined" onClick={() => navigate(-1)}>Back</Button>
            </Box>
            <Typography variant="h4" gutterBottom>Influencer Insight — Details</Typography>

            {/* Profile & Balance Card */}
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                    <Grid container spacing={3} alignItems="center">
                        <Grid item>
                            <Avatar
                                src={influencer.picture}
                                alt={influencer.name}
                                sx={{ width: 100, height: 100 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md>
                            <Typography variant="h5" fontWeight="bold">{influencer.name}</Typography>
                            <Typography color="textSecondary">{influencer.email}</Typography>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <Typography variant="subtitle2" color="textSecondary">Current Inflyx Score</Typography>
                                <Typography variant="h4" color="primary" fontWeight="bold">
                                    {txResult.currentScore != null ? txResult.currentScore : (scoreDetails?.result?.inflyxScore ?? "N/A")}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3} md={2}>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                                <Typography variant="subtitle2" color="textSecondary">Current Balance</Typography>
                                <Typography variant="h4" color="secondary" fontWeight="bold">
                                    {txResult.currentBalance != null ? txResult.currentBalance : (scoreDetails?.result?.inflyxCoin ?? "N/A")}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Transaction History */}
            <Typography variant="h6" gutterBottom>Transaction History</Typography>
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
                <CardContent>
                    {isTransactionsLoading ? (
                        <Typography>Loading transactions...</Typography>
                    ) : transactions.length > 0 ? (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Date</strong></TableCell>
                                        <TableCell><strong>Type</strong></TableCell>
                                        <TableCell align="right"><strong>Amount</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Score at Time</strong></TableCell>
                                        <TableCell><strong>Note</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {transactions.map((tx, index) => (
                                        <TableRow key={tx.id || index}>
                                            <TableCell>{formatDate(tx.created_at)}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={tx.transaction_type}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getTypeColor(tx.transaction_type),
                                                        color: '#fff',
                                                        fontWeight: 600
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    fontWeight="bold"
                                                    color={tx.transaction_type === 'Credit' ? 'green' : tx.transaction_type === 'Debit' ? 'red' : 'orange'}
                                                >
                                                    {tx.transaction_type === 'Credit' ? '+' : '-'}{tx.amount}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={tx.status} color={getStatusColor(tx.status)} size="small" variant="outlined" />
                                            </TableCell>
                                            <TableCell>{tx.influx_score_at_time ?? "N/A"}</TableCell>
                                            <TableCell>{tx.admin_note || "—"}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
                            No transactions found for this influencer.
                        </Typography>
                    )}

                    {txResult.total > limit && (
                        <Box display="flex" justifyContent="center" mt={2}>
                            <Button
                                variant="outlined"
                                disabled={page === 0}
                                onClick={() => setPage(p => p - 1)}
                                sx={{ mr: 1 }}
                            >
                                Previous
                            </Button>
                            <Typography sx={{ display: 'flex', alignItems: 'center', mx: 2 }}>
                                Page {page + 1} of {Math.ceil(txResult.total / limit)}
                            </Typography>
                            <Button
                                variant="outlined"
                                disabled={(page + 1) * limit >= txResult.total}
                                onClick={() => setPage(p => p + 1)}
                            >
                                Next
                            </Button>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default InfluencerInsightDetails;
